import { useState, useEffect } from 'react'
import { DetailHeader } from '@/components/DetailHeader'
import { ImageGallery } from '@/components/ImageGallery'
import { Tag } from '@/components/Tag'
import { Icon } from '@/components/Icon'
import { DetailTextSection } from '@/components/DetailTextSection'
import { DetailTable } from '@/components/DetailTable'
import type { DetailTableRow } from '@/components/DetailTable'
import { PricingPanel } from '@/components/PricingPanel'
import type { OutboundLink } from '@/components/PricingPanel'
import { ApplyInterstitialModal } from '@/components/ApplyInterstitialModal'
import { MapPlaceholder, MAP_HEIGHT_CLASS } from '@/components/MapPlaceholder'
import { Button } from '@/components/Button'
import { Disclaimer } from '@/components/Disclaimer'
import { Link } from '@/components/Link'
import { ContactLink } from '@/components/ContactLink'
import { NoResultsIllustration } from '@/components/NoResultsIllustration/NoResultsIllustration'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { fetchUnitDetail, fetchPortfolioUnitDetail, ApiError } from '@/api/fetchCollection'
import { getOptions, getLabels, getConfig, isPortfolio, setActiveAssetConfig } from '@/data/config'
import { formatUnitName, getUnitProperty, hasLeaseTerm, resolvePricingDisplay, AVAILABILITY_CONTACT_MESSAGE } from '@/data/mappers'
import { emitUnitDetailsClick, emitUnitApplyClick } from '@/metrics/metrics'
import { buildApplyUrl, getInitialApplyUrl } from '@/utils/applyUrl'
import { formatAvailableDate, defaultMoveInDate } from '@/utils/formatDate'
import { linkTargetProps } from '@/utils/linkTarget'
import { isUnitOnNotice, onNoticeTagLabel } from '@/utils/onNotice'
import type { ApiUnit, LeaseOverride } from '@/types/api'

interface UnitDetailProps {
	unitId: string
	onBack: () => void
	onUnitClick?: (unitId: string) => void
	onShare: () => void
	onGallery: (images: Array<{ src: string; caption?: string }>, index: number, title?: string) => void
	onTour: (url: string, title: string) => void
	onLeaseOptions: (unit: ApiUnit) => void
	onCalculator: (url: string) => void
	onPlanClick?: (planId: string) => void
	mockUnit?: ApiUnit | null
	mockUnits?: ApiUnit[]
	leaseOverride?: LeaseOverride
}

export function UnitDetail ({
	unitId,
	onBack,
	onUnitClick,
	onShare,
	onGallery,
	onTour,
	onLeaseOptions,
	onCalculator,
	onPlanClick,
	mockUnit,
	mockUnits,
	leaseOverride
}: UnitDetailProps) {
	const [unit, setUnit] = useState<ApiUnit | null>(null)
	const [loading, setLoading] = useState(true)
	const [notFound, setNotFound] = useState(false)
	const [loadError, setLoadError] = useState(false)
	const [reloadKey, setReloadKey] = useState(0)
	const [galleryIndex, setGalleryIndex] = useState(0)
	const [interstitialOpen, setInterstitialOpen] = useState(false)
	const isDesktop = useMediaQuery('(min-width: 768px)')

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		setNotFound(false)
		setLoadError(false)
		setUnit(null)
		setGalleryIndex(0)

		if (mockUnit !== undefined) {
			if (mockUnit) {
				setUnit(mockUnit)
			} else {
				setNotFound(true)
			}
			setLoading(false)
			return
		}

		const request = isPortfolio()
			? fetchPortfolioUnitDetail(unitId).then(res => {
				if (!cancelled) setActiveAssetConfig(res.config)
				return res
			})
			: fetchUnitDetail(unitId)

		request
			.then(res => {
				if (!cancelled) {
					setUnit(res.data)
					setLoading(false)
				}
			})
			.catch(err => {
				if (!cancelled) {
					if (err instanceof ApiError && err.status === 404) setNotFound(true)
					else setLoadError(true)
					setLoading(false)
				}
			})

		return () => { cancelled = true }
	}, [unitId, mockUnit, reloadKey])

	// Fire the details.click metric once the unit detail view has loaded.
	useEffect(() => {
		if (unit) emitUnitDetailsClick(unit)
	}, [unit])

	const opts = getOptions()
	const labels = getLabels()

	if (loading) {
		return (
			<div className='spaces-app spaces-unit-detail spaces:bg-canvas-app spaces:min-h-screen'>
				<div className='spaces:flex spaces:items-center spaces:justify-center spaces:py-80'>
					<span className='spaces:text-16/24 spaces:font-default spaces:text-content-3'>Loading…</span>
				</div>
			</div>
		)
	}

	if (loadError || notFound || !unit) {
		return (
			<div className='spaces-app spaces-unit-detail spaces:bg-canvas-app spaces:min-h-screen'>
				<Disclaimer variant='top' text={labels.disclaimerTop} className='spaces:mb-16' />
				<div className='spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-24 spaces:overflow-hidden'>
					<DetailHeader
						backLabel={isDesktop ? 'Back to Search' : 'Back'}
						onBack={onBack}
					/>
					<div
						role={loadError ? 'alert' : undefined}
						className={`${loadError ? 'spaces-unit-load-error' : 'spaces-unit-not-found'} spaces:flex spaces:flex-col spaces:items-center spaces:justify-center spaces:gap-16 spaces:py-80 spaces:px-16`}
					>
						<NoResultsIllustration />
						<h2 className='spaces:text-18/24 spaces:font-semi spaces:text-content-1'>
							{loadError
								? `Something went wrong loading this ${labels.unitLabel.toLowerCase()}.`
								: 'Unit is no longer available.'}
						</h2>
						<p className='spaces:text-14/24 spaces:font-default spaces:text-content-3'>
							{loadError ? 'Please check your connection and try again.' : 'Return to search to see other available options.'}
						</p>
						<div className='spaces:flex spaces:flex-wrap spaces:justify-center spaces:gap-8'>
							{loadError && (
								<Button
									appearance='solid'
									color='brand'
									size='md'
									label='Retry'
									spacesClass='spaces-retry'
									onClick={() => { setReloadKey(k => k + 1) }}
								/>
							)}
							<Button
								appearance={loadError ? 'ghost' : 'solid'}
								color={loadError ? 'content2' : 'brand'}
								size='md'
								label='Back to Search'
								onClick={onBack}
							/>
						</div>
					</div>
				</div>
				<Disclaimer variant='bottom' text={labels.disclaimer} link={labels.disclaimerLink} className='spaces:mt-16' />
			</div>
		)
	}

	const fp = unit.floor_plan
	const unitName = formatUnitName(unit.unit_number)

	const isOnNotice = isUnitOnNotice(unit)

	// Build gallery images
	const galleryImages = buildGalleryImages(unit)
	const galleryImageSrcs = galleryImages.map(img => img.src)

	// Pricing logic (mirrors mapUnitToCardProps)
	const priceIsHidden = isOnNotice || unit.price_hidden || opts.hidePa || opts.hideStartingAt ||
		isPriceHidden(unit.price) ||
		!hasLeaseTerm(unit.default_lease_term)

	const isRevManagement = opts.useRevManagement && opts.applyProvider !== 'None'

	let totalPrice = ''
	let baseRent: string | undefined
	let hasFees = true
	if (!priceIsHidden) {
		const mode = resolvePricingDisplay(unit.pricing_display, unit.engrain_asset_id)
		if (leaseOverride) {
			// Apply lease override from user selection
			if (mode === 'total' || mode === 'both') {
				totalPrice = leaseOverride.priceWithFeesString
			} else {
				totalPrice = leaseOverride.baseRentString
			}
			if (mode === 'both') {
				baseRent = leaseOverride.baseRentString
			}
			hasFees = mode === 'total' || mode === 'both'
		} else if (mode === 'total' || mode === 'both') {
			totalPrice = unit.price_with_fees_string
			if (mode === 'both') {
				baseRent = unit.price_string
			}
		} else {
			totalPrice = unit.price_string
			hasFees = false
		}
	}

	// Tags
	const unitTags = (Array.isArray(unit.tags) ? unit.tags : []).map(t => t.name)
	const planTags = (Array.isArray(fp?.tags) ? fp.tags : []).map(t => t.name)
	const allTags = [...unitTags, ...planTags]

	// Specials
	const specialMessage = unit.specials ?? undefined

	// Virtual tour
	const tourUrl = unit.unit_tour_url || fp?.tour_url || null
	const hasVirtualTour = !!tourUrl

	// Amenities
	const unitAmenities = (Array.isArray(unit.amenities) ? unit.amenities : []).map(a => a.amenity_label)
	const planAmenities = (Array.isArray(fp?.amenities) ? fp.amenities : []).map(a => a.amenity_label)

	// Apply CTA visibility
	const showApply = !isOnNotice && !opts.hidePa && !opts.hideApplyCta &&
		unit.apply_provider !== 'None' && unit.apply_provider !== 'none' && unit.apply_provider !== null &&
		!unit.hide_apply_link

	// Only rendered when the price is shown, which requires hasLeaseTerm() to pass.
	const leaseTerm = `${leaseOverride?.selectedTerm ?? unit.default_lease_term} Mo.`

	// Available date / move-in date for pricing panel.
	// The move-in date is user-selectable; default to today when the unit's
	// available date is in the past, otherwise default to the future available date.
	const moveInDate = leaseOverride?.moveInDate ?? defaultMoveInDate(unit.available_on)

	// Calculator
	const calculatorEnabled = opts.enableSightmapCalculator && !!getConfig().sightmapEmbedUrl

	// Outbound link
	const outboundLinks: OutboundLink[] = []
	if (opts.outboundLink) {
		outboundLinks.push({ label: opts.outboundLink.label, href: opts.outboundLink.url })
	}

	// Build apply URL — provider-specific params for lease term + move-in date
	const applyUrl = (() => {
		if (!unit.apply_url) return ''
		const provider = opts.applyProvider
		if (leaseOverride) {
			return buildApplyUrl(unit.apply_url, provider, leaseOverride)
		}
		if (isRevManagement) {
			return getInitialApplyUrl(unit.apply_url, provider, unit.default_lease_term)
		}
		return unit.apply_url
	})()

	const canApply = showApply && !!applyUrl

	// When the interstitial is enabled, clicking Apply opens a confirmation modal
	// before forwarding to the external apply URL; otherwise forward immediately.
	const handleApply = () => {
		emitUnitApplyClick(unit)
		if (opts.applyInterstitial) {
			setInterstitialOpen(true)
		} else {
			window.open(applyUrl, '_blank', 'noopener,noreferrer')
		}
	}

	return (
		<div className='spaces-app spaces-unit-detail spaces:bg-canvas-app spaces:min-h-screen'>
			<Disclaimer variant='top' text={labels.disclaimerTop} className='spaces:mb-16' />

			<div className='spaces:flex spaces:flex-col spaces:md:flex-row spaces:gap-16 spaces:items-stretch spaces:md:items-start'>
				{/* Left panel — max-width matches the ResultsLayout split list column; flexbox gives the leftover width to the map. */}
				<div className='spaces:flex-1 spaces:min-w-0 spaces:max-w-[720px] spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-24 spaces:overflow-hidden'>
					<DetailHeader
						backLabel={isDesktop ? 'Back to Search' : 'Back'}
						onBack={onBack}
						onShare={onShare}
					/>

					<div className='spaces:flex spaces:flex-col spaces:gap-24 spaces:p-16 spaces:md:p-24'>
						{/* Visuals */}
						{galleryImageSrcs.length > 0 && (
							<ImageGallery
								images={galleryImageSrcs}
								label={unitName}
								currentIndex={galleryIndex}
								onIndexChange={setGalleryIndex}
								onOpen={() => { onGallery(galleryImages, galleryIndex, unit?.unit_number) }}
								hasVirtualTour={hasVirtualTour}
								onVirtualTour={() => { if (tourUrl) onTour(tourUrl, unitName) }}
							/>
						)}

						{/* Summary */}
						<div className='spaces-unit-detail-summary spaces:flex spaces:flex-col spaces:gap-4'>
							<h2 className='spaces:text-18/24 spaces:font-strong spaces:text-content-1'>
								{unitName}
							</h2>
							{fp && (
								<div className='spaces-unit-detail-attributes spaces:overflow-hidden'>
									<div className='spaces:-ml-13 spaces:flex spaces:flex-wrap spaces:items-center spaces:gap-y-4'>
										<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
											<Icon name='Bed' size={24} className='spaces:text-content-4' />
											<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>{fp.bedroom_count_string_short}</span>
										</div>
										<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
											<Icon name='Bath' size={24} className='spaces:text-content-4' />
											<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>{fp.bathroom_count_string_short}</span>
										</div>
										<div className='spaces:flex spaces:items-center spaces:gap-4 spaces:border-l spaces:border-canvas-2 spaces:pl-12 spaces:pr-12'>
											<Icon name='Area' size={24} className='spaces:text-content-4' />
											<span className='spaces:text-16/24 spaces:font-default spaces:text-content-2'>
												{Number(unit.area).toLocaleString()} ft<sup className='spaces:text-10 spaces:font-strong'>2</sup>
											</span>
										</div>
									</div>
								</div>
							)}
						</div>

						{/* Specials banner */}
						{specialMessage && (
							<div className='spaces-unit-detail-specials spaces:flex spaces:items-center spaces:gap-12 spaces:bg-success-subtle spaces:rounded-16 spaces:py-8 spaces:pl-12 spaces:pr-8 spaces:overflow-hidden'>
								<Icon name='Tag' size={24} className='spaces:shrink-0 spaces:text-success-on-subtle' />
								<span className='spaces:text-16/24 spaces:font-default spaces:text-success-on-subtle'>{specialMessage}</span>
							</div>
						)}

						{/* Pricing panel — no lease bar when pricing is hidden */}
						{priceIsHidden ? (
							<PricingPanel
								totalPrice=''
								priceFallback={
									<div className='spaces-unit-detail-overview spaces:flex'>
										<ContactLink postText='for Pricing' size='lg' />
									</div>
								}
								showApply={false}
							/>
						) : (
							<PricingPanel
								moveInDate={moveInDate}
								leaseTerm={leaseTerm}
								totalPrice={totalPrice}
								hasFees={hasFees}
								baseRent={baseRent}
								onEditLease={isRevManagement && !unit.available_date_hidden ? () => onLeaseOptions(unit) : undefined}
								showApply={canApply}
								primaryLabel='Apply'
								onApply={canApply ? handleApply : undefined}
								outboundLinks={outboundLinks}
								onCalculator={calculatorEnabled ? () => {
									const sightmapEmbedUrl = getConfig().sightmapEmbedUrl
									const lt = leaseOverride?.selectedTerm?.toString() ?? unit.default_lease_term?.toString() ?? ''
									const mid = leaseOverride?.moveInDate ?? defaultMoveInDate(unit.available_on)
									const params = new URLSearchParams()
									params.set('unit_number', unit.unit_number)
									params.set('lease_term', lt)
									params.set('move_in_date', mid)
									params.set('mode', 'expense_calculator')
									onCalculator(`${sightmapEmbedUrl}?${params.toString()}`)
								} : undefined}
							/>
						)}

						{/* Sub-details */}
						{(() => {
							const subDetailRows: DetailTableRow[] = []
							if (isOnNotice || allTags.length > 0) {
								subDetailRows.push({
									label: 'Tags',
									valueClassName: 'spaces:flex spaces:flex-wrap spaces:gap-8 spaces:items-start spaces:p-8',
									value: (
										<>
											{isOnNotice && (
												<Tag label={onNoticeTagLabel(unit)} color='brand' appearance='subtle' size='md' />
											)}
											{unitTags.map(t => (
												<Tag key={`ut-${t}`} label={t} color='brand' appearance='subtle' size='md' />
											))}
											{planTags.map(t => (
												<Tag key={`pt-${t}`} label={t} color='brand' appearance='subtle' size='md' />
											))}
										</>
									)
								})
							}
							if (isPortfolio() && unit.spaces_asset_name) {
								const pageLink = getUnitProperty(unit)?.spacesPageLink
								subDetailRows.push({
									label: 'Property',
									value: pageLink
										? (
											<Link
												label={unit.spaces_asset_name}
												href={pageLink.url}
												{...linkTargetProps(pageLink.target)}
												defaultUnderline
												spacesClass='spaces-unit-detail-community'
											/>
											)
										: unit.spaces_asset_name
								})
							}
							if (fp) {
								subDetailRows.push({
									label: 'Floor Plan',
									value: onPlanClick
										? <Link label={fp.name} href={`#plan/${fp.id}`} defaultUnderline onClick={(e) => { e.preventDefault(); onPlanClick(String(fp.id)) }} />
										: fp.name
								})
								subDetailRows.push({
									label: 'Bedrooms',
									value: fp.bedroom_count_string_short
								})
								subDetailRows.push({
									label: 'Bathrooms',
									value: fp.bathroom_count_string_short
								})
							}
							subDetailRows.push({
								label: 'Area',
								value: (
									<span className='spaces:text-16/24 spaces:font-default spaces:text-content-1'>
										{Number(unit.area).toLocaleString()} ft<sup className='spaces:text-10 spaces:font-strong'>2</sup>
									</span>
								)
							})
							if (!unit.hide_available_date && (isOnNotice || unit.available_date_hidden)) {
								subDetailRows.push({
									label: 'Available',
									value: AVAILABILITY_CONTACT_MESSAGE
								})
							} else if (!unit.hide_available_date && unit.available_on) {
								subDetailRows.push({
									label: 'Available',
									value: formatAvailableDate(unit.available_on)
								})
							}
							if (unit.floor_label) {
								subDetailRows.push({
									label: 'Floor',
									value: unit.floor_label
								})
							}
							return (
								<div className='spaces-unit-sub-details'>
									<DetailTable rows={subDetailRows} />
								</div>
							)
						})()}

						{/* Overview */}
						{fp?.description && (
							<DetailTextSection title='Overview' content={fp.description} />
						)}

						{/* Divider between Overview and Features */}
						{fp?.description && (unitAmenities.length > 0 || planAmenities.length > 0) && (
							<div className='spaces:h-px spaces:bg-canvas-3' />
						)}

						{/* Features */}
						{opts.consolidatedAmenities ? (
							(unitAmenities.length > 0 || planAmenities.length > 0) && (
								<div className='spaces-unit-detail-amenities'>
									<DetailTextSection
										title='Features'
										listItems={[...unitAmenities, ...planAmenities]}
									/>
								</div>
							)
						) : (
							<>
								{unitAmenities.length > 0 && (
									<div className='spaces-unit-detail-amenities'>
										<DetailTextSection title='Unit Features' listItems={unitAmenities} />
									</div>
								)}
								{planAmenities.length > 0 && (
									<div className='spaces-unit-detail-amenities'>
										<DetailTextSection title='Floor Plan Features' listItems={planAmenities} />
									</div>
								)}
							</>
						)}

						{!isDesktop && <MapPlaceholder locateUnitId={unit.id} locateFloorId={unit.floor_id} onUnitClick={onUnitClick} mockUnits={mockUnits} className='spaces:h-[436px]' />}
					</div>
				</div>

				{/* Right panel — map */}
				<div className={`spaces:hidden spaces:md:block spaces:flex-1 spaces:min-w-[400px] spaces:sticky spaces:top-16 ${MAP_HEIGHT_CLASS}`}>
					<MapPlaceholder locateUnitId={unit.id} locateFloorId={unit.floor_id} onUnitClick={onUnitClick} mockUnits={mockUnits} className='spaces:h-full' />
				</div>
			</div>

			<Disclaimer variant='bottom' text={labels.disclaimer} link={labels.disclaimerLink} className='spaces:mt-16' />

			<ApplyInterstitialModal
				open={interstitialOpen}
				onClose={() => setInterstitialOpen(false)}
				onContinue={() => {
					setInterstitialOpen(false)
					window.open(applyUrl, '_blank', 'noopener,noreferrer')
				}}
				content={labels.applyInterstitialContent}
			/>
		</div>
	)
}

/* ---------- Helpers ---------- */

const FPO_IMAGE = '/wp-content/plugins/ecs-spaces/public/images/spaces-fpo.png'

function isPriceHidden (price: number | null, priceString?: string): boolean {
	if (price === null || price === 0) return true
	if (priceString === 'Call for pricing') return true
	return false
}

function buildGalleryImages (unit: ApiUnit): Array<{ src: string; caption?: string }> {
	const images: Array<{ src: string; caption?: string }> = []
	const fp = unit.floor_plan

	// Primary image
	const primary = fp?.primary_image_cdn ?? FPO_IMAGE
	images.push({ src: primary, caption: fp?.primary_image_caption ?? undefined })

	// Alternate image
	if (fp?.alternate_image_cdn) {
		images.push({ src: fp.alternate_image_cdn, caption: fp?.alternate_image_caption ?? undefined })
	}

	// Floor plan gallery
	if (fp?.gallery && Array.isArray(fp.gallery)) {
		for (const img of fp.gallery) {
			if (typeof img === 'string') {
				images.push({ src: img })
			} else if (img && typeof img === 'object' && 'src' in img) {
				images.push({ src: String((img as { src: string }).src) })
			}
		}
	}

	// Unit gallery
	if (unit.gallery && Array.isArray(unit.gallery)) {
		for (const img of unit.gallery) {
			if (typeof img === 'string') {
				images.push({ src: img })
			} else if (img && typeof img === 'object' && 'src' in img) {
				images.push({ src: String((img as { src: string }).src) })
			}
		}
	}

	return images
}
